/*
 * ASKING FOR A WORD OR A SENTENCE, IN THE PAGE (2026-09-30).
 *
 * The replacement for `window.prompt`. The embedded browsers a kiosk, PWA
 * shell or the Electron app run in answer prompt() with null without showing
 * anything, so a "Name for the new library" or "Reason for rejecting" silently
 * came back empty and the action behind it did nothing. Build standard,
 * section 5: never window.confirm, alert or prompt.
 *
 * Same shape as useConfirm, so a caller converts the same way: keep your
 * handler, ask, and render the dialog once.
 *
 *   const { askText, askTextDialog } = useAskText();
 *   ...
 *   onClick={() => askText({ title: 'New library', label: 'Name', required: true,
 *                            onSubmit: (name) => createMut.mutate({ name }) })}
 *   ...
 *   {askTextDialog}
 *
 * Built on Dialog, so it closes the standard three ways; closing is Cancel.
 */
import { useState, type JSX } from 'react';
import { Dialog } from './Dialog';

export interface AskTextRequest {
  title: string;
  /** The question, shown above the field. */
  label: string;
  initialValue?: string;
  placeholder?: string;
  /** Default 'Save'. */
  confirmLabel?: string;
  /** true: the button stays off until something other than spaces is typed. */
  required?: boolean;
  /** A box for a sentence or two rather than a single line. */
  multiline?: boolean;
  /** Runs with the trimmed text, only when the person confirms. */
  onSubmit: (value: string) => void;
  onCancel?: () => void;
  /** For a box opened from something above the standard overlays — the chat
   * panel sits at 1200. Default: the overlay's own 999. */
  zIndex?: number;
}

export interface UseAskText {
  askText: (request: AskTextRequest) => void;
  /** Render this once in the component's tree. Null while nothing is asked. */
  askTextDialog: JSX.Element | null;
}

export function useAskText(): UseAskText {
  const [request, setRequest] = useState<AskTextRequest | null>(null);
  const [value, setValue] = useState('');

  const askText = (r: AskTextRequest): void => {
    setValue(r.initialValue ?? '');
    setRequest(r);
  };

  const cancel = (): void => {
    request?.onCancel?.();
    setRequest(null);
  };

  const canSubmit = !request?.required || value.trim() !== '';
  const submit = (): void => {
    if (!request || !canSubmit) return;
    const r = request;
    setRequest(null);
    r.onSubmit(value.trim());
  };

  const askTextDialog = request ? (
    <Dialog
      title={request.title}
      onClose={cancel}
      testId="ask-text"
      zIndex={request.zIndex}
      width={480}
      footer={
        <>
          <button type="button" className="btn" onClick={cancel} data-testid="ask-text-cancel">
            Cancel
          </button>
          <button
            type="button"
            className="btn pri"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="ask-text-submit"
          >
            {request.confirmLabel ?? 'Save'}
          </button>
        </>
      }
    >
      <label className="fl" htmlFor="ask-text-input">
        {request.label}
      </label>
      {request.multiline ? (
        <textarea
          id="ask-text-input"
          className="fi"
          rows={3}
          value={value}
          placeholder={request.placeholder}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          data-testid="ask-text-input"
        />
      ) : (
        <input
          id="ask-text-input"
          className="fi"
          value={value}
          placeholder={request.placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          autoFocus
          data-testid="ask-text-input"
        />
      )}
    </Dialog>
  ) : null;

  return { askText, askTextDialog };
}
